import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { venuesAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { MapPin, Plus, ChevronRight, Activity } from 'lucide-react';

export default function Venues() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [venues, setVenues] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [newVenue, setNewVenue] = useState({
    name: '',
    address: '',
    courts: 1,
    description: '',
  });

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const response = await venuesAPI.getAll();
        setVenues(response.data || []);
      } catch (error) {
        console.error('Error fetching venues:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVenues();
  }, []);

  const handleCreateVenue = async () => {
    if (!newVenue.name.trim() || !newVenue.address.trim()) {
      toast.error('Name and address are required');
      return;
    }

    setCreating(true);
    try {
      const response = await venuesAPI.create({
        name: newVenue.name,
        address: newVenue.address,
        courts: parseInt(newVenue.courts),
        description: newVenue.description || null,
      });
      
      setVenues([response.data, ...venues]);
      setCreateDialogOpen(false);
      setNewVenue({ name: '', address: '', courts: 1, description: '' });
      toast.success('Venue created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create venue');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="venues-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Venues</h1>
            <p className="text-muted-foreground mt-1">
              {venues.length} venue{venues.length !== 1 ? 's' : ''} registered
            </p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-secondary hover:bg-secondary/90" data-testid="create-venue-btn"
                onClick={(e) => { if (!isAuthenticated) { e.preventDefault(); navigate('/login'); } }}>
                <Plus className="mr-2 h-4 w-4" />
                Add Venue
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Venue</DialogTitle>
                <DialogDescription>
                  Register a badminton court or facility
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Venue Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Shuttle Arena"
                    value={newVenue.name}
                    onChange={(e) => setNewVenue({ ...newVenue, name: e.target.value })}
                    data-testid="venue-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="address">Address *</Label>
                  <Input
                    id="address"
                    placeholder="e.g., 123 Sports Complex, City"
                    value={newVenue.address}
                    onChange={(e) => setNewVenue({ ...newVenue, address: e.target.value })}
                    data-testid="venue-address-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="courts">Number of Courts</Label>
                  <Input
                    id="courts"
                    type="number"
                    min="1"
                    max="20"
                    value={newVenue.courts}
                    onChange={(e) => setNewVenue({ ...newVenue, courts: e.target.value })}
                    data-testid="venue-courts-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Additional details about the venue..."
                    value={newVenue.description}
                    onChange={(e) => setNewVenue({ ...newVenue, description: e.target.value })}
                    data-testid="venue-description-input"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateVenue}
                  disabled={creating}
                  className="bg-secondary hover:bg-secondary/90"
                  data-testid="submit-venue-btn"
                >
                  {creating ? 'Creating...' : 'Add Venue'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Venues Grid */}
        {venues.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No venues yet. Add one to get started!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {venues.map((venue) => (
              <Card key={venue.venue_id} className="hover:shadow-md hover:border-primary/50 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-lg bg-secondary/10 flex items-center justify-center">
                        <MapPin className="h-6 w-6 text-secondary" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-lg">{venue.name}</h3>
                        <p className="text-sm text-muted-foreground">{venue.courts} court{venue.courts !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-4">{venue.address}</p>
                  
                  {venue.description && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {venue.description}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Activity className="h-4 w-4" />
                      <span>{venue.matches_played || 0} matches played</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
